import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { PaginatedEnsueCollection } from "../../../../../ensue-react-system/utilities/paginated-ensue-collection";
import { StatusEnum } from "../../../../../commons/enums/status-enum";
import { MatchStatusEnum } from "../../../../../commons/enums/match-status-enum";
import { manageFootballSeasonService } from "../../../../../commons";
import { SortOrderEnum } from "../../../../../commons/enums/sort-order-enum";
import { FootballMatchDetail } from "../../../../../commons/models/football/football-match-detail";

export interface IFootballManageMatchOption {
  seasonId: number;
  urlParams: {
    page?: number;
    perPage?: number;
    sort?: string;
    sortOrder?: string;
    competitionId?: number;
    teamId?: number;
    teamIds?: string;
    status?: MatchStatusEnum;
    afterDate?: string;
    beforeDate?: string;
  };
}

interface IFootBallMatchStore {
  matchList: IMatchStore;
  lastFiveMatchList: IMatchStore;
  headToHeadMatchList: IMatchStore;
}

interface IMatchStore {
  data: PaginatedEnsueCollection<FootballMatchDetail>;
  status?: StatusEnum;
  error?: string | null;
}

const initialState: IFootBallMatchStore = {
  matchList: {
    data: new PaginatedEnsueCollection<FootballMatchDetail>(),
    status: StatusEnum.Idle,
  },
  headToHeadMatchList: {
    data: new PaginatedEnsueCollection<FootballMatchDetail>(),
    status: StatusEnum.Idle,
  },
  lastFiveMatchList: {
    data: new PaginatedEnsueCollection<FootballMatchDetail>(),
    status: StatusEnum.Idle,
  },
};

const footballManageMatchListSlice = createSlice({
  name: "manage-match-list",
  initialState: initialState,
  reducers: {
    cleanUpManageMatchList(state) {
      state.matchList = {
        data: new PaginatedEnsueCollection<FootballMatchDetail>(),
        status: StatusEnum.Idle,
      };
    },
    cleanUpManageHeadToHeadMatchList(state) {
      state.headToHeadMatchList = {
        data: new PaginatedEnsueCollection<FootballMatchDetail>(),
        status: StatusEnum.Idle,
      };
    },
    cleanUpManageLastFiveMatchList(state) {
      state.lastFiveMatchList = {
        data: new PaginatedEnsueCollection<FootballMatchDetail>(),
        status: StatusEnum.Idle,
      };
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchManageMatchList.pending, (state) => {
        state.matchList.status = StatusEnum.Loading;
      })
      .addCase(fetchManageMatchList.fulfilled, (state, action) => {
        state.matchList.status = StatusEnum.Succeeded;
        state.matchList.data = action.payload as PaginatedEnsueCollection<FootballMatchDetail>;
      })
      .addCase(fetchManageMatchList.rejected, (state) => {
        state.matchList.status = StatusEnum.Failed;
      })
      .addCase(fetchManageHeadToHeadMatches.pending, (state) => {
        state.headToHeadMatchList.status = StatusEnum.Loading;
      })
      .addCase(fetchManageHeadToHeadMatches.fulfilled, (state, action) => {
        state.headToHeadMatchList.status = StatusEnum.Succeeded;
        state.headToHeadMatchList.data = action.payload as PaginatedEnsueCollection<FootballMatchDetail>;
      })
      .addCase(fetchManageHeadToHeadMatches.rejected, (state) => {
        state.headToHeadMatchList.status = StatusEnum.Failed;
      })
      .addCase(fetchManageLastFiveMatches.pending, (state) => {
        state.lastFiveMatchList.status = StatusEnum.Loading;
      })
      .addCase(fetchManageLastFiveMatches.fulfilled, (state, action) => {
        state.lastFiveMatchList.status = StatusEnum.Succeeded;
        state.lastFiveMatchList.data = action.payload as PaginatedEnsueCollection<FootballMatchDetail>;
      })
      .addCase(fetchManageLastFiveMatches.rejected, (state) => {
        state.lastFiveMatchList.status = StatusEnum.Failed;
      });
  },
});

export const fetchManageMatchList = createAsyncThunk(
  "matches/fetchManageMatchList",
  async (options: IFootballManageMatchOption, thunkAPI) => {
    try {
      const matchList = await manageFootballSeasonService.getSeasonMatchList(options.seasonId, options.urlParams);
      return matchList.map(item=>({...item,competitionId:item.competition?.id}));
    } catch (e) {
      return thunkAPI.rejectWithValue(e);
    }
  }
);

export const fetchManageHeadToHeadMatches = createAsyncThunk(
  "matches/fetchManageHeadToHeadMatches",
  async (options: IFootballManageMatchOption, thunkAPI) => {
    let urlParams = {
      status: MatchStatusEnum.Finished,
      perPage: 5,
      ...options.urlParams,
    };
    try {
      const headToHeadMatchList = await manageFootballSeasonService.getSeasonMatchList(options.seasonId, urlParams);
      return headToHeadMatchList;
    } catch (e) {
      return thunkAPI.rejectWithValue(e);
    }
  }
);

export const fetchManageLastFiveMatches = createAsyncThunk(
  "matches/fetchManageLastFiveMatches",
  async (options: IFootballManageMatchOption, thunkAPI) => {
    let urlParams = {
      status: MatchStatusEnum.Finished,
      sort: "scheduledAt",
      sortOrder: SortOrderEnum.Descending,
      perPage: 5,
      ...options.urlParams,
    };
    try {
      const lastFiveMatchList = await manageFootballSeasonService.getSeasonMatchList(options.seasonId, urlParams);
      return lastFiveMatchList;
    } catch (e) {
      return thunkAPI.rejectWithValue(e);
    }
  }
);

export const { cleanUpManageMatchList, cleanUpManageHeadToHeadMatchList, cleanUpManageLastFiveMatchList } =
  footballManageMatchListSlice.actions;
export default footballManageMatchListSlice.reducer;
